/**
 * The sidebar tree's reorder rules, pure (the hook is `useTreeReorder`).
 *
 * A row has drop zones, as in the legacy tree (`getDropPosition`): its top
 * band places the dragged item BEFORE it, its bottom band AFTER it, and a
 * collection's middle moves the item INTO it (the folder drop target's own
 * 300ms hover arm). Two rules keep the insertion line honest:
 *
 * - Only an item of the row's own kind gets the bands. The server keeps
 *   collections and files in separate sort orders (every collection, then
 *   every file), so a file "between two collections" cannot land there.
 * - An EXPANDED collection has no bottom band: its children follow it, so a
 *   line under its row would point inside it. Its lower part moves into it.
 */

import type { DragEvent } from 'react';
import type { HierarchyNode } from '@bower/shared/types/api';

import {
  getDropPosition,
  isItemDrag,
  type DraggedItem,
  type DropPosition,
} from '../../navigation/HierarchyTree.dnd';
import { moveRefusal, readOnlyRefusal } from '../../workspace/dnd/dropRules';
import type { TreeRow } from './useFolderTreeRows';

export type ReorderPosition = 'before' | 'after';

/**
 * Where `dragged` would land beside `row`, with the pointer at `relY` (0 at
 * the row's top, 1 at its bottom). Null: no reorder here, so the row's own
 * folder drop target (or nothing) takes the drag.
 */
export function treeDropZone(
  relY: number,
  row: TreeRow,
  dragged: DraggedItem,
): ReorderPosition | null {
  if (dragged.id === row.item.id || dragged.kind !== row.item.kind) return null;
  if (!row.isFolder) return getDropPosition(relY, true) === 'before' ? 'before' : 'after';
  if (row.isExpanded) return relY < 0.25 ? 'before' : null;
  const position = getDropPosition(relY, false);
  return position === 'on' ? null : position;
}

/** The nodes of the level `row` sits in, in the order the tree draws them. */
export function levelNodes(rows: ReadonlyArray<TreeRow>, parentId: string | null): HierarchyNode[] {
  return rows.filter((r) => r.parentId === parentId).map((r) => r.node);
}

/** The level's nodes of `dragged`'s kind: the only ones a reorder key can sit between. */
export function sameKindNodes(
  nodes: ReadonlyArray<HierarchyNode>,
  dragged: DraggedItem,
): HierarchyNode[] {
  return nodes.filter((n) => (n.type === 'project') === (dragged.kind === 'folder'));
}

/** The row's band under the pointer, for `dragged` (a one-item drag only). */
export function zoneOf(
  event: DragEvent<HTMLElement>,
  row: TreeRow,
  dragged: ReadonlyArray<DraggedItem>,
): ReorderPosition | null {
  if (dragged.length !== 1 || !isItemDrag(event.dataTransfer)) return null;
  const rect = event.currentTarget.getBoundingClientRect();
  if (rect.height <= 0) return null;
  return treeDropZone((event.clientY - rect.top) / rect.height, row, dragged[0]);
}

/** The parent row `row` sits under (null at the root). */
export function parentRowOf(rows: ReadonlyArray<TreeRow>, row: TreeRow): TreeRow | null {
  return row.parentId === null ? null : (rows.find((r) => r.item.id === row.parentId) ?? null);
}

/** Why placing `dragged` beside `row` is refused, or null. Same rules as a folder drop. */
export function placementRefusal(
  rows: ReadonlyArray<TreeRow>,
  dragged: DraggedItem,
  row: TreeRow,
): string | null {
  if (dragged.parentId === row.parentId) return null;
  const parent = parentRowOf(rows, row);
  if (!parent) {
    return row.item.canEdit
      ? null
      : readOnlyRefusal({ id: null, name: 'the workspace', canEdit: false });
  }
  const ancestors = new Set((parent.item.location ?? []).map((f) => f.id));
  return moveRefusal([dragged], parent.item, ancestors);
}

/**
 * The two neighbours a fractional-index reorder needs, read off the sibling
 * list the drop actually landed in.
 *
 * The dragged node is excluded before indexing. When it is already adjacent to
 * the target, including it makes the node its own neighbour and
 * `generateKeyBetween` just re-derives the key it already has — a silent no-op
 * instead of a move. Excluding it is also what lets the cross-parent case share
 * this helper, where the dragged node isn't in `siblings` at all.
 *
 * An edge with no neighbour (above the first row, below the last) yields
 * `undefined`, which the reorder endpoint reads as "open-ended".
 *
 * Known limitation (paged levels): `siblings` holds only the LOADED pages. In a
 * level that still has a "Show more" page, a drop after the last loaded row
 * sends no `beforeId`, so the server keys it after that row with an open upper
 * bound, `generateKeyBetween(afterKey, null)`. That key can sort among the
 * unloaded rows rather than exactly where it was dropped. Accepted: loading
 * the rest first places it exactly.
 *
 * Exported for tests.
 */
export function resolveDropNeighbours({
  siblings,
  targetNodeId,
  position,
  draggedNodeId,
}: {
  siblings: ReadonlyArray<HierarchyNode>;
  targetNodeId: string;
  position: DropPosition;
  draggedNodeId: string;
}): { afterId: string | undefined; beforeId: string | undefined } {
  const none = { afterId: undefined, beforeId: undefined };
  // 'on' re-parents rather than placing between siblings — it has no neighbours.
  if (position === 'on') return none;

  const others = siblings.filter((s) => s.id !== draggedNodeId);
  const targetIdx = others.findIndex((s) => s.id === targetNodeId);
  if (targetIdx === -1) return none;

  return position === 'after'
    ? { afterId: targetNodeId, beforeId: others[targetIdx + 1]?.id }
    : { afterId: others[targetIdx - 1]?.id, beforeId: targetNodeId };
}

/**
 * The sibling order a same-parent before/after drop paints optimistically:
 * the dragged sibling taken out, then put back beside the target. A dragged
 * id that is not in the list leaves it as it was (a copy). Exported for tests.
 */
export function reorderSiblings<T extends { readonly id: string }>(
  siblings: ReadonlyArray<T>,
  draggedId: string,
  targetId: string,
  position: 'before' | 'after',
): T[] {
  const reordered = [...siblings];
  const fromIdx = reordered.findIndex((n) => n.id === draggedId);
  if (fromIdx !== -1) {
    const [moved] = reordered.splice(fromIdx, 1);
    const newTargetIdx = reordered.findIndex((n) => n.id === targetId);
    const insertAt = position === 'before' ? newTargetIdx : newTargetIdx + 1;
    reordered.splice(Math.max(0, insertAt), 0, moved);
  }
  return reordered;
}
